import React, { useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { Drawer } from "antd";
import { useRateCardsActions } from "tap/store/rateCards/useRateCards.js";
import { useToasts } from "tap/utils/hooks/useToasts.js";
import { useLoading } from "tap/utils/hooks/useLoading.js";
import { tapApi } from "tap/api.js";
import { useCreateRateCardForm } from "./hooks/useCreateRateCardForm";
import {
  validateRateCardForm,
  getPercentagesEnabled,
} from "./utils/validation";
import { buildCreateRateCardPayload } from "./utils/payloadBuilder";
import BasicInfoForm from "./rate-card-form/basic-info";
import TiersForm from "./rate-card-form/tiers";
import { RateCardDrawerFooter } from "./components/RateCardDrawerFooter";
import "./styles.scss";

/**
 * CreateRateCardDrawer component for creating new rate cards.
 */
export function CreateRateCardDrawer() {
  const [validationErrors, setValidationErrors] = useState({});

  const { isCreateDrawerOpen } = useSelector((state) => state.rateCards);
  const { updateRateCards, clearRateCardForm } = useRateCardsActions();
  const { showSuccessToast, showErrorToast } = useToasts();
  const { setLoadingTrue, setLoadingFalse } = useLoading();

  const {
    formData,
    tierFormats,
    currencyOptions,
    formatOptions,
    windowSequenceOptions,
    priceTypeOptions,
    revShareOptions,
    handleInputChange,
    handleAddTierFormat,
    handleRemoveTierFormat,
    handleTierFormatChange,
    handleAddTierRow,
    handleRemoveTierRow,
    handleTierRowInputChange,
    showTierFormat,
    priceType,
  } = useCreateRateCardForm();

  const handleClose = () => {
    setValidationErrors({});
    updateRateCards({ isCreateDrawerOpen: false });
  };

  const handleAfterOpenChange = useCallback(
    (open) => {
      if (open) return;
      clearRateCardForm();
    },
    [clearRateCardForm],
  );

  const hasValidationErrors = Object.keys(validationErrors).length > 0;

  const handleSave = async () => {
    const percentagesEnabled = getPercentagesEnabled(formData, true);
    const { isValid, errors } = validateRateCardForm(
      formData,
      "create",
      percentagesEnabled,
    );

    if (!isValid) {
      setValidationErrors(errors);
      return;
    }

    setValidationErrors({});

    const payload = buildCreateRateCardPayload(formData);

    setLoadingTrue();
    try {
      await tapApi.createRateCard(payload);
      showSuccessToast("Rate Card created successfully.");
      updateRateCards({ refreshRateCards: true });
      handleClose();
    } catch (error) {
      showErrorToast(error?.message || "Error creating Rate Card.");
      console.error("Error creating rate card:", error);
    } finally {
      setLoadingFalse();
    }
  };

  return (
    <Drawer
      className="create-drawer"
      title="Create Rate Card"
      width="95vw"
      placement="right"
      open={isCreateDrawerOpen}
      onClose={handleClose}
      afterOpenChange={handleAfterOpenChange}
      bodyStyle={{ paddingBottom: 80 }}
      footer={
        <RateCardDrawerFooter
          hasValidationErrors={hasValidationErrors}
          onClose={handleClose}
          onSave={handleSave}
        />
      }
    >
      <div className="rate-card-form-container">
        <BasicInfoForm
          formData={formData}
          mode="create"
          handleInputChange={handleInputChange}
          currencyOptions={currencyOptions}
          windowSequenceOptions={windowSequenceOptions}
          priceTypeOptions={priceTypeOptions}
          revShareOptions={revShareOptions}
          validationErrors={validationErrors}
        />
        <div className="rate-card-form-tiers-section" style={{ marginTop: 32 }}>
          <TiersForm
            formData={formData}
            mode="create"
            tierFormats={tierFormats}
            formatOptions={formatOptions}
            handleTierFormatChange={handleTierFormatChange}
            handleInputChange={handleInputChange}
            handleAddTierFormat={handleAddTierFormat}
            handleRemoveTierFormat={handleRemoveTierFormat}
            handleTierRowInputChange={handleTierRowInputChange}
            handleAddTierRow={handleAddTierRow}
            handleRemoveTierRow={handleRemoveTierRow}
            validationErrors={validationErrors}
            showTierRows={showTierFormat && !!priceType}
          />
        </div>
      </div>
    </Drawer>
  );
}
